import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { toast } from 'sonner';

import { humanErrorMessage } from '@/api/errors';
import { useClients } from '@/clients/queries';
import { DataTable } from '@/components/data-table';
import { FormDialog } from '@/components/form-dialog';
import { PageShell } from '@/components/page-shell';
import { StatusDot } from '@/components/status-dot';
import { Button } from '@/components/ui/button';
import { useCreateWarehouse, useWarehouses, type WarehouseRecord } from '@/warehouses/queries';
import { WarehouseCreateSchema, type WarehouseCreateInput } from '@/warehouses/schemas';
import { WarehouseFormFields } from '@/warehouses/warehouse-form';

const formatDate = (iso: string): string => new Date(iso).toLocaleString('ru-RU');

export const WarehousesListPage = () => {
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading } = useWarehouses({ includeDeleted });
  const { data: clients } = useClients();
  const createWarehouse = useCreateWarehouse();

  const clientNameById = new Map((clients ?? []).map((c) => [c.id, c.name]));

  const columns: ColumnDef<WarehouseRecord>[] = [
    {
      id: 'name',
      header: 'Название',
      cell: ({ row }) => (
        <Link
          to="/warehouses/$warehouseId"
          params={{ warehouseId: row.original.id }}
          className="font-medium hover:underline"
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      id: 'client',
      header: 'Клиент',
      cell: ({ row }) => clientNameById.get(row.original.clientId) ?? row.original.clientId,
    },
    {
      id: 'status',
      header: 'Статус',
      cell: ({ row }) => <StatusDot domain="warehouse" state={row.original.status} />,
    },
    {
      id: 'createdAt',
      header: 'Создан',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{formatDate(row.original.createdAt)}</span>
      ),
    },
  ];

  const handleSubmit = (values: WarehouseCreateInput): void => {
    createWarehouse.mutate(values, {
      onSuccess: () => {
        toast.success('Склад создан');
        setCreateOpen(false);
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  return (
    <div className="space-y-4 p-6">
      <PageShell
        title="Склады"
        actions={
          <>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={includeDeleted}
                onChange={(e) => {
                  setIncludeDeleted(e.target.checked);
                }}
                className="h-4 w-4"
              />
              Показывать удалённых
            </label>
            <Button
              onClick={() => {
                setCreateOpen(true);
              }}
            >
              Создать склад
            </Button>
          </>
        }
      >
        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">Загрузка…</div>
        ) : (
          <DataTable columns={columns} data={data ?? []} emptyMessage="Нет складов" />
        )}
      </PageShell>

      <FormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Новый склад"
        schema={WarehouseCreateSchema}
        defaultValues={{ clientId: '', name: '', description: '', status: 'active' }}
        onSubmit={handleSubmit}
        isPending={createWarehouse.isPending}
      >
        <WarehouseFormFields mode="create" />
      </FormDialog>
    </div>
  );
};
